import React, { useMemo, useCallback, useState } from "react"
import { Table } from "antd"
import PropTypes from "prop-types"
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import SortableTableRow, { DragHandleCell, SortableDisabledContext } from "ui/SortableTableRow"
import crewMembersColumns from "./columns"

const dragHandleColumn = {
  key: "drag",
  width: 40,
  render: () => <DragHandleCell />
}

const CrewMembersTable = ({ data, onChange, loading, onEdit, onDelete, onDeactivate, onActivate, onAssignClick, onReorder, dragDisabled }) => {
  const [liveIds, setLiveIds] = useState(null)
  const liveIdsRef = React.useRef(null)
  const lastOverIdRef = React.useRef(null)

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  )

  const dataColumns = crewMembersColumns(onEdit, onDelete, onDeactivate, onActivate, onAssignClick)
  const columns = useMemo(() => [dragHandleColumn, ...dataColumns], [dataColumns])

  const baseIds = useMemo(() => (data || []).map((item) => String(item.key ?? item.id)), [data])
  const sortableIds = liveIds ?? baseIds

  const liveData = useMemo(() => {
    if (!liveIds) return data
    const map = new Map((data || []).map((item) => [String(item.key ?? item.id), item]))
    return liveIds.map((id) => map.get(id)).filter(Boolean)
  }, [liveIds, data])

  const handleDragOver = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return
      if (lastOverIdRef.current === String(over.id)) return
      lastOverIdRef.current = String(over.id)
      const ids = liveIdsRef.current ?? baseIds
      const oldIndex = ids.indexOf(String(active.id))
      const newIndex = ids.indexOf(String(over.id))
      if (oldIndex === -1 || newIndex === -1) return
      const next = arrayMove(ids, oldIndex, newIndex)
      liveIdsRef.current = next
      setLiveIds(next)
    },
    [baseIds]
  )

  const handleDragEnd = useCallback(
    ({ active }) => {
      const finalIds = liveIdsRef.current
      liveIdsRef.current = null
      lastOverIdRef.current = null
      setLiveIds(null)
      if (!finalIds) return
      const activeId = String(active.id)
      const oldIndex = baseIds.indexOf(activeId)
      const newIndex = finalIds.indexOf(activeId)
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return
      const overId = baseIds[newIndex]
      if (!overId) return
      onReorder?.(activeId, overId)
    },
    [onReorder, baseIds]
  )

  const handleDragCancel = useCallback(() => {
    liveIdsRef.current = null
    lastOverIdRef.current = null
    setLiveIds(null)
  }, [])

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <SortableDisabledContext.Provider value={Boolean(dragDisabled)}>
          <Table
            columns={columns}
            dataSource={liveData}
            pagination={false}
            onChange={onChange}
            loading={loading}
            rowKey={(record) => String(record.key ?? record.id)}
            components={{ body: { row: SortableTableRow } }}
            scroll={{ x: "max-content" }}
          />
        </SortableDisabledContext.Provider>
      </SortableContext>
    </DndContext>
  )
}

CrewMembersTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onDeactivate: PropTypes.func,
  onActivate: PropTypes.func,
  onAssignClick: PropTypes.func,
  onReorder: PropTypes.func,
  dragDisabled: PropTypes.bool
}

export default React.memo(CrewMembersTable)
